/**
 * Activity Service
 * Handles activity data operations
 */

import { graphqlClient } from '../core/graphqlClient';
import { apiClient } from '../core/apiClient';
import {
  GET_ACTIVITIES_DASHBOARD,
  GET_ACTIVITIES_COUNT,
  SEARCH_ACTIVITIES_DASHBOARD,
  SEARCH_ACTIVITIES_COUNT,
  GET_ACTIVITY_AUDIT_LOG,
} from '../resources/activityGquery';

export interface ActivityDashboard {
  activity_id: number;
  activity_name: string;
  created_by: number;
  created_by_name: string;
  group_name: string;
  is_active: boolean;
  patient_group_id: number;
  updated_by: number;
  updated_by_name: string;
  user_group_id: number;
  workflow_id: number;
  workflow_name: string;
}

export interface ActivityResult {
  activities: ActivityDashboard[];
  totalCount: number;
}

export interface VisibilityWindow {
  visibilityWindowId?: number;
  dayOfWeek: number;
  fromTime: string;
  toTime: string;
}

export interface ActivityConfig {
  allowedFromDate: string;
  allowedToDate: string;
  timezone: string;
  visibilityWindows: VisibilityWindow[];
}

export interface GetActivityConfigResponse {
  data: {
    activityConfigId?: number;
    activityId?: number;
    allowedFromDate: string;
    allowedToDate: string;
    timezone: string;
    visibilityWindows: VisibilityWindow[];
  };
  success: boolean;
  message: string;
  timestamp: number;
}

export interface CreateActivityPayload {
  userGroupId?: number;
  patientGroupId: number;
  initialWorkflowId: number;
  activityName: string;
  activityConfigId?: number;
  activityId?: number;
  activityConfig: ActivityConfig;
}

export interface ActivityAuditLog {
  pep_activity_audit_log_id: number;
  activity_id: number;
  action_type: string;
  action_datetime: string;
  action_by: string;
  activity_data: any;
  metadata: any;
}

class ActivityService {
  async getActivities(
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<ActivityResult> {
    try {
      const [activitiesResponse, countResponse] = await Promise.all([
        graphqlClient.query(
          GET_ACTIVITIES_DASHBOARD,
          { offset, limit },
          hasuraToken
        ),
        graphqlClient.query(GET_ACTIVITIES_COUNT, {}, hasuraToken),
      ]);

      if (!activitiesResponse.success) {
        throw new Error('Failed to fetch activities');
      }

      if (!countResponse.success) {
        throw new Error('Failed to fetch activities count');
      }

      const activities = activitiesResponse.data?.pep_vw_activity_dashboard || [];
      const totalCount = countResponse.data?.pep_vw_activity_dashboard_aggregate?.aggregate?.count || 0;

      return {
        activities,
        totalCount,
      };
    } catch (error) {
      console.error('Error fetching activities:', error);
      throw error;
    }
  }

  async searchActivities(
    searchValue: string,
    offset: number,
    limit: number,
    hasuraToken: string
  ): Promise<ActivityResult> {
    try {
      const searchPattern = `%${searchValue}%`;

      const [activitiesResponse, countResponse] = await Promise.all([
        graphqlClient.query(
          SEARCH_ACTIVITIES_DASHBOARD,
          { searchValue: searchPattern, offset, limit },
          hasuraToken
        ),
        graphqlClient.query(
          SEARCH_ACTIVITIES_COUNT,
          { searchValue: searchPattern },
          hasuraToken
        ),
      ]);

      if (!activitiesResponse.success) {
        throw new Error('Failed to search activities');
      }

      if (!countResponse.success) {
        throw new Error('Failed to fetch activities count');
      }

      const activities = activitiesResponse.data?.pep_vw_activity_dashboard || [];
      const totalCount = countResponse.data?.pep_vw_activity_dashboard_aggregate?.aggregate?.count || 0;

      return {
        activities,
        totalCount,
      };
    } catch (error) {
      console.error('Error searching activities:', error);
      throw error;
    }
  }

  async createActivity(
    healthcareCenterId: number,
    payload: CreateActivityPayload,
    token: string
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `/api/ngpep/${healthcareCenterId}/activity`,
        payload,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create activity');
      }

      return response.data;
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  }

  async updateActivity(
    healthcareCenterId: number,
    activityId: number,
    payload: CreateActivityPayload,
    token: string
  ): Promise<any> {
    try {
      const response = await apiClient.put(
        `/api/ngpep/${healthcareCenterId}/activity/${activityId}`,
        payload,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update activity');
      }

      return response.data;
    } catch (error) {
      console.error('Error updating activity:', error);
      throw error;
    }
  }

  async deleteActivity(
    healthcareCenterId: number,
    activityId: number,
    token: string
  ): Promise<any> {
    try {
      const response = await apiClient.delete(
        `/api/ngpep/${healthcareCenterId}/activity/${activityId}`,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete activity');
      }

      return response.data;
    } catch (error) {
      console.error('Error deleting activity:', error);
      throw error;
    }
  }

  async deleteAppointmentSlots(
    healthcareCenterId: number,
    mapperIds: number[],
    token: string
  ): Promise<any> {
    try {
      const response = await apiClient.request(
        `/api/ngpep/${healthcareCenterId}/appointment-slots`,
        {
          method: 'DELETE',
          token,
          body: { mapperIds }
        }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete appointment slots');
      }

      return response.data;
    } catch (error) {
      console.error('Error deleting appointment slots:', error);
      throw error;
    }
  }

  async getActivityAuditLog(
    activityId: number,
    hasuraToken: string
  ): Promise<{ logs: ActivityAuditLog[]; totalCount: number }> {
    try {
      const response = await graphqlClient.query(
        GET_ACTIVITY_AUDIT_LOG,
        { activityId },
        hasuraToken
      );

      if (!response.success) {
        throw new Error('Failed to fetch activity audit log');
      }

      const logs = response.data?.pep_activity_audit_log || [];
      const totalCount = response.data?.pep_activity_audit_log_aggregate?.aggregate?.count || 0;

      return { logs, totalCount };
    } catch (error) {
      console.error('Error fetching activity audit log:', error);
      throw error;
    }
  }

  async getActivityConfig(
    healthcareCenterId: number,
    activityId: number,
    token: string
  ): Promise<GetActivityConfigResponse['data']> {
    try {
      const response = await apiClient.get(
        `/api/ngpep/${healthcareCenterId}/activity/${activityId}/config`,
        token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch activity config');
      }

      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error fetching activity config:', error);
      throw error;
    }
  }
}

export const activityService = new ActivityService();
